"use client";

import { useCallback, useRef, useState } from "react";
import { api, ApiError } from "@/lib/api";

/**
 * Drag-and-drop image uploader. Accepts:
 *   - Drag-drop a local file
 *   - Click to open the system file picker
 *   - Paste from clipboard
 *
 * On upload it calls /v1/admin/uploads/image-presigned, gets a
 * short-lived presigned PUT URL, then PUTs the file bytes directly to
 * DO Spaces with `x-amz-acl: public-read`. Returns the resulting public
 * URL via the `onUploaded` callback.
 *
 * Falls back to a plain text input when uploads are disabled
 * (presign endpoint returns 503) so admins can still paste an
 * external URL — no need to wait for the bucket provisioning to
 * finish before authoring markets.
 *
 * Sizes/types validated client-side per the same rules the backend
 * enforces (5 MiB cap; jpeg/png/webp/gif). Failures land per-attempt
 * so the admin doesn't lose context on retry.
 */

export interface ImageDropzoneProps {
  scope: "markets" | "posts" | "avatars";
  value: string | null;
  onChange: (url: string | null) => void;
  /** Optional alt-text rendered in the preview <img>. */
  alt?: string;
  /** Optional className for the outer wrapper. */
  className?: string;
  /** Inline label above the dropzone. */
  label?: string;
}

const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];
const MAX_BYTES = 5 * 1024 * 1024;

export function ImageDropzone({
  scope,
  value,
  onChange,
  alt = "",
  className = "",
  label,
}: ImageDropzoneProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploadInFlight, setUploadInFlight] = useState(false);
  const [progress, setProgress] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [uploadsDisabled, setUploadsDisabled] = useState(false);
  const [manualUrl, setManualUrl] = useState<string>(value ?? "");

  const upload = useCallback(
    async (file: File) => {
      setError(null);
      if (!ALLOWED_TYPES.includes(file.type)) {
        setError(
          `Unsupported type ${file.type}. Allowed: ${ALLOWED_TYPES.join(", ")}.`,
        );
        return;
      }
      if (file.size > MAX_BYTES) {
        setError(
          `File too large (${(file.size / 1024 / 1024).toFixed(1)} MiB). Max ${MAX_BYTES / 1024 / 1024} MiB.`,
        );
        return;
      }
      setUploadInFlight(true);
      setProgress(0);
      try {
        const presigned = await api.adminPresignImageUpload({
          scope,
          contentType: file.type,
          contentLength: file.size,
        });
        // Direct PUT to Spaces. XMLHttpRequest is used (not fetch) so
        // we get upload progress events.
        await new Promise<void>((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open("PUT", presigned.uploadUrl, true);
          xhr.setRequestHeader("Content-Type", file.type);
          xhr.setRequestHeader("x-amz-acl", "public-read");
          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) {
              setProgress(Math.round((e.loaded / e.total) * 100));
            }
          };
          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve();
            } else {
              reject(
                new Error(
                  `Spaces PUT failed: HTTP ${xhr.status} ${xhr.statusText}. Body: ${xhr.responseText.slice(0, 200)}`,
                ),
              );
            }
          };
          xhr.onerror = () =>
            reject(new Error("Network error while uploading to Spaces."));
          xhr.send(file);
        });
        onChange(presigned.publicUrl);
        setManualUrl(presigned.publicUrl);
      } catch (err) {
        if (err instanceof ApiError && err.status === 503) {
          setUploadsDisabled(true);
          setError(
            "Image uploads not enabled for this environment yet. Paste a URL instead.",
          );
        } else {
          setError(
            err instanceof ApiError
              ? err.message
              : err instanceof Error
                ? err.message
                : String(err),
          );
        }
      } finally {
        setUploadInFlight(false);
      }
    },
    [scope, onChange],
  );

  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (!f) {
      // Some browsers expose dragged remote URLs via DataTransfer.
      // text/uri-list is the canonical form.
      const uri = e.dataTransfer.getData("text/uri-list");
      if (uri && /^https?:\/\//.test(uri.trim())) {
        const cleaned = uri.split("\n")[0].trim();
        onChange(cleaned);
        setManualUrl(cleaned);
        return;
      }
      const text = e.dataTransfer.getData("text/plain");
      if (text && /^https?:\/\//.test(text.trim())) {
        const cleaned = text.trim();
        onChange(cleaned);
        setManualUrl(cleaned);
        return;
      }
      return;
    }
    void upload(f);
  }

  function onPaste(e: React.ClipboardEvent<HTMLDivElement>) {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (let i = 0; i < items.length; i += 1) {
      const it = items[i];
      if (it.kind === "file") {
        const f = it.getAsFile();
        if (f) {
          e.preventDefault();
          void upload(f);
          return;
        }
      }
    }
  }

  function clear() {
    setError(null);
    setManualUrl("");
    onChange(null);
  }

  return (
    <div className={className}>
      {label && (
        <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.4px] text-[var(--color-text3)]">
          {label}
        </label>
      )}
      <div
        role="region"
        aria-label="Image upload"
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onPaste={onPaste}
        onClick={() => !uploadInFlight && inputRef.current?.click()}
        className={`relative cursor-pointer rounded-md border ${
          dragOver
            ? "border-[var(--color-brand)] bg-[var(--color-brand-tint)]"
            : "border-dashed border-[var(--color-border)] bg-[var(--color-bg)]"
        } px-3 py-4 text-center text-[12px] text-[var(--color-text2)] transition-colors hover:border-[var(--color-text2)]`}
        tabIndex={0}
      >
        {value ? (
          <div className="flex items-center gap-3">
            <img
              src={value}
              alt={alt}
              className="h-16 w-24 shrink-0 rounded-md border border-[var(--color-border)] object-cover"
              loading="lazy"
            />
            <div className="min-w-0 flex-1 text-left">
              <p className="truncate text-[11px] text-[var(--color-text)]">
                {value}
              </p>
              <p className="mt-0.5 text-[10px] text-[var(--color-text3)]">
                Drag a new image here, click to replace, or paste from
                clipboard.
              </p>
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                clear();
              }}
              className="rounded-md border border-[var(--color-border)] px-2 py-1 text-[11px] font-bold text-[var(--color-text2)] hover:text-[var(--color-text)]"
            >
              Remove
            </button>
          </div>
        ) : uploadInFlight ? (
          <div>
            <p className="font-bold text-[var(--color-text)]">
              Uploading… {progress}%
            </p>
            <div className="mx-auto mt-2 h-1 max-w-[200px] overflow-hidden rounded-full bg-[var(--color-surface)]">
              <div
                className="h-full bg-[var(--color-brand)] transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        ) : (
          <div>
            <p className="font-bold text-[var(--color-text)]">
              Drop an image here, paste from clipboard, or click to select.
            </p>
            <p className="mt-0.5 text-[10px] text-[var(--color-text3)]">
              JPG / PNG / WebP / GIF · max 5 MiB
            </p>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={ALLOWED_TYPES.join(",")}
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void upload(f);
            e.target.value = "";
          }}
        />
      </div>

      {error && (
        <p
          role="alert"
          className="mt-1 text-[11px]"
          style={{ color: "var(--color-no)" }}
        >
          {error}
        </p>
      )}

      {/* Manual URL fallback — always available so admins can paste an
          external URL (Imgur / Wikipedia / etc.) without uploading. */}
      <div className="mt-2 flex items-center gap-2">
        <input
          type="url"
          value={manualUrl}
          onChange={(e) => setManualUrl(e.target.value)}
          onBlur={() => {
            const trimmed = manualUrl.trim();
            if (trimmed === "" && value === null) return;
            if (trimmed !== value) {
              onChange(trimmed === "" ? null : trimmed);
            }
          }}
          placeholder={
            uploadsDisabled
              ? "Paste an image URL (uploads not yet enabled in this env)"
              : "Or paste an image URL"
          }
          className="flex-1 rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-2.5 py-1 text-[12px] text-[var(--color-text)] outline-none focus:border-[var(--color-brand)]"
        />
      </div>
    </div>
  );
}
